import React, { useEffect, useState } from 'react';
import { Grid, Box, Button, Card, CardContent, Divider, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import FormData from 'form-data';
import { useNavigate, useParams } from 'react-router-dom';
import { TabContext, TabPanel } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { setNestedObjectValues } from 'formik';
import { useSelector } from 'react-redux';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { styles } from './styles';
import PositionDetailsTab from './PositionDetailsTab';
import DemandAttributes from '../../demandAttributes';
import {
    usePreModifyPositionMutation,
    usePreModifyPositionWithCompaFieldsMutation,
    useModifyPositionMutation,
    useModifyPositionWithCompaFieldsMutation,
} from '../../../api/positions';
import { useCustomAttributesFormik, usePersonalDetailsFormik } from './formik';
import { hasPermission } from '../../../utils/permission';
import { allowedPositionActions } from '../Positions/PositionMenu';

const views = [
    {
        label: 'Position Details',
        value: 'position-details',
    },
    {
        label: 'Custom Attributes',
        value: 'custom-attributes',
    },
];

const ModifyPosition = (props) => {
    const [view, setView] = useState('position-details');
    const { user } = useSelector((state) => state.session);
    const [positionDetails, setPositionDetails] = useState({});
    const [preCreationDetails, setPreCreationDetails] = useState();
    const params = useParams();
    const navigate = useNavigate();

    const authorities = useSelector((state) => state.session.user.authorities);

    const userHasCompaModifyPermisson = hasPermission(authorities, [
        'ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPA_UPDATE_POSITION',
    ]);

    const personalDetailsFormik = usePersonalDetailsFormik(preCreationDetails?.position, (value) =>
        handleBasicDetails(value),
    );

    const { customAttributesFormik, metaCustomFieldDefinitions: metaCustomFields } = useCustomAttributesFormik(
        preCreationDetails?.extendedDef,
        preCreationDetails?.customObjectInstance,
        (value) => handleCustomDetails(value),
    );

    const { enqueueSnackbar } = useSnackbar();

    const [preModifyPosition, { isLoading }] = usePreModifyPositionMutation();
    const [preModifyPositionWithCompaFields, { isLoading: isPreModifyWithCompaLoading }] =
        usePreModifyPositionWithCompaFieldsMutation();
    const [modifyPosition, { isLoading: isModifyLoading }] = useModifyPositionMutation();
    const [modifyPositionWithCompaFields, { isLoading: isModifyWithCompaLoading }] =
        useModifyPositionWithCompaFieldsMutation();

    const isPreModifyLoading = isLoading || isPreModifyWithCompaLoading;

    useEffect(() => {
        const bodyFormData = new FormData();
        bodyFormData.append('id', params.id);
        async function fetchPreCreationData() {
            try {
                const response = userHasCompaModifyPermisson
                    ? await preModifyPositionWithCompaFields(bodyFormData).unwrap()
                    : await preModifyPosition(bodyFormData).unwrap();

                if (!response) return;
                if (response.status === 'ERROR') throw new Error(response?.message);
                if (allowedPositionActions('single', 'modify', response?.position, { user })) {
                    setPreCreationDetails(response);
                } else {
                    navigate('/access-denied');
                }
            } catch (error) {
                if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                else enqueueSnackbar(error?.message || 'Failed to fetch position data', { variant: 'error' });
            }
        }
        fetchPreCreationData();
    }, [params.id, userHasCompaModifyPermisson, preModifyPosition, preModifyPositionWithCompaFields, enqueueSnackbar]);

    const handleBasicDetails = (details) => {
        setPositionDetails(details);
        setView('custom-attributes');
    };

    const handleCustomDetails = async (details) => {
        const { submit, ...filteredData } = {
            ...positionDetails,
        };
        const customAttributesMetaData = [
            {
                attribute: 'customObjectInstance.customerID',
                value: metaCustomFields.customerID,
            },
            {
                attribute: 'customObjectInstance.baseObjectMetaClassName',
                value: metaCustomFields.baseObjectMetaClassName,
            },
            {
                attribute: 'customObjectInstance.metaCustomObjectDefinitionUID',
                value: metaCustomFields.metaCustomObjectDefinitionID,
            },
        ];

        let index = 0;
        (metaCustomFields?.metaCustomFieldDefinitions || []).forEach((customField) => {
            customAttributesMetaData.push(
                {
                    attribute: `customObjectInstance.customFieldValues[${index}].fieldValue`,
                    value: details[customField.fieldLabel] || '',
                },
                {
                    attribute: `customObjectInstance.customFieldValues[${index}].metaCustomFieldDefinitionID`,
                    value: customField.metaCustomFieldDefinitionID,
                },
            );
            index++;
        });

        const bodyFormData = new FormData();
        bodyFormData.append('id', params.id);
        delete filteredData?.['startDate'];
        for (const key in filteredData) {
            bodyFormData.append(key, filteredData[key]);
        }

        customAttributesMetaData.forEach((customAttribute) => {
            bodyFormData.append(customAttribute.attribute, customAttribute.value);
        });

        try {
            const response = userHasCompaModifyPermisson
                ? await modifyPositionWithCompaFields(bodyFormData).unwrap()
                : await modifyPosition(bodyFormData).unwrap();

            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);

            enqueueSnackbar('Position updated successfully', { variant: 'success' });
            navigate('/positions');
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Failed to modify position', { variant: 'error' });
        }
    };

    const validateTabChange = async (value) => {
        const tabFormikMap = {
            'position-details': personalDetailsFormik,
            'custom-attributes': customAttributesFormik,
        };

        const errors = await tabFormikMap[view].validateForm();
        if (Object.keys(errors || {}).length === 0) {
            setView(value);
        } else {
            tabFormikMap[view].setTouched(setNestedObjectValues(errors, true));
        }
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Positions</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card {...props}>
                    <CardContent>
                        {isPreModifyLoading ? (
                            <Box
                                sx={{
                                    py: 4,
                                }}
                            >
                                <Skeleton height={42} />
                                <Skeleton height={42} />
                                <Skeleton height={42} />
                            </Box>
                        ) : (
                            <>
                                <Grid container direction="column" spacing={2}>
                                    <Grid item>
                                        <Button
                                            startIcon={<ChevronLeftIcon />}
                                            variant="text"
                                            color="secondary"
                                            onClick={() => navigate('/positions')}
                                        >
                                            Back
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Typography color="textPrimary" variant="h4">
                                            {`Modify Position Details - ${preCreationDetails?.position?.positionCode}`}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <TabContext value={view}>
                                            <Box>
                                                <Tabs
                                                    value={view}
                                                    onChange={(event, value) => validateTabChange(value)}
                                                    aria-label="basic tabs example"
                                                >
                                                    {views.map((option, index) => (
                                                        <Tab
                                                            disabled={false}
                                                            label={option.label}
                                                            value={option.value}
                                                            key={index}
                                                        />
                                                    ))}
                                                </Tabs>
                                                <Divider sx={styles.divider} />
                                            </Box>
                                            <TabPanel sx={styles.tabpanel} value={'position-details'}>
                                                <PositionDetailsTab
                                                    isModify
                                                    preCreationDetails={preCreationDetails}
                                                    formik={personalDetailsFormik}
                                                />
                                            </TabPanel>
                                            <TabPanel sx={styles.tabpanel} value={'custom-attributes'}>
                                                <DemandAttributes
                                                    isModify
                                                    formik={customAttributesFormik}
                                                    metaCustomFields={metaCustomFields}
                                                />
                                            </TabPanel>
                                        </TabContext>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default ModifyPosition;
